/**
 * AppHeader — the single header bar for every screen with a back affordance + title.
 * Sticky, transparent, no background band. Glyph '‹' (amber) + 24px display title.
 * Extracted from MeSubpageShell so all subpages + the flow screens share one look.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './AppHeader.module.css';

export interface AppHeaderProps {
  /** Already-translated short section title. */
  title: string;
  /** Back action. Defaults to navigate(-1). */
  onBack?: () => void;
  /** data-testid on the back button. Omitted → no attribute (matches existing flow screens). */
  backTestId?: string;
  /** id on the <h1>, referenced by the host's aria-labelledby. Must be unique per mounted instance. */
  headingId?: string;
  /** Optional action at the right edge of the bar (e.g. an add button). Absent → the bar is unchanged. */
  right?: ReactNode;
  /** One line with an ellipsis instead of wrapping (long user-supplied names). */
  singleLineTitle?: boolean;
  /** A tab's top screen has nothing to go back to: no back button. */
  hideBack?: boolean;
}

export function AppHeader({
  title,
  onBack,
  backTestId,
  headingId = 'app-header-heading',
  right,
  singleLineTitle = false,
  hideBack = false,
}: AppHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const handleBack = (): void => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };
  return (
    <div className={styles.topBar}>
      {!hideBack && (
        <button
          type="button"
          className={styles.backBtn}
          onClick={handleBack}
          aria-label={t('actions.back')}
          data-testid={backTestId}
        >
          <span aria-hidden="true">‹</span>
        </button>
      )}
      <h1 id={headingId} className={singleLineTitle ? `${styles.title} ${styles.titleSingleLine}` : styles.title}>
        {title}
      </h1>
      {right ? <div className={styles.right}>{right}</div> : null}
    </div>
  );
}
